import React, { useState, useEffect, useContext } from 'react';
import { useParams, Link } from 'react-router-dom';
import axios from 'axios';
import { AuthContext } from '../App';
import { Button } from './ui/button';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Badge } from './ui/badge';
import { Textarea } from './ui/textarea';
import { Avatar, AvatarFallback, AvatarImage } from './ui/avatar';
import { Alert, AlertDescription } from './ui/alert';
import { 
  Heart, 
  MessageCircle, 
  User, 
  Calendar, 
  BookOpen,
  ChevronLeft,
  ChevronRight,
  Send,
  ArrowLeft,
  Share2,
  Tag,
  X,
  List,
  ArrowUp,
  ArrowDown,
  Maximize2,
  Minimize2,
  Edit,
  Plus
} from 'lucide-react';
import { toast } from 'sonner';

const ComicView = () => {
  const { id } = useParams();
  const { user, token, API } = useContext(AuthContext);
  const [comic, setComic] = useState(null);
  const [seriesComics, setSeriesComics] = useState([]);
  const [comments, setComments] = useState([]);
  const [newComment, setNewComment] = useState('');
  const [liked, setLiked] = useState(false);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [currentEpisodeIndex, setCurrentEpisodeIndex] = useState(0);
  const [loading, setLoading] = useState(true);
  const [submittingComment, setSubmittingComment] = useState(false);
  const [readerMode, setReaderMode] = useState(false);
  const [showEpisodeList, setShowEpisodeList] = useState(false);

  useEffect(() => {
    fetchComic();
    fetchComments();
    if (user) {
      checkLikeStatus();
    }
  }, [id, user]);

  useEffect(() => {
    if (comic && comic.series_id) {
      fetchSeriesEpisodes();
    }
  }, [comic]);

  const fetchComic = async () => {
    try {
      const response = await axios.get(`${API}/comics/${id}`);
      setComic(response.data);
    } catch (error) {
      console.error('Error fetching comic:', error);
      toast.error('Failed to load comic');
    } finally {
      setLoading(false);
    }
  };

  const fetchComments = async () => {
    try {
      const response = await axios.get(`${API}/comics/${id}/comments`);
      setComments(response.data);
    } catch (error) {
      console.error('Error fetching comments:', error);
    }
  };

  const fetchSeriesEpisodes = async () => {
    try {
      const response = await axios.get(`${API}/comics?series_id=${comic.series_id}`);
      const sortedEpisodes = response.data.sort((a, b) => a.episode_number - b.episode_number);
      setSeriesComics(sortedEpisodes);
      
      // Find current episode index
      const currentIndex = sortedEpisodes.findIndex(ep => ep.id === id);
      setCurrentEpisodeIndex(currentIndex);
    } catch (error) {
      console.error('Error fetching series episodes:', error);
    }
  };

  const checkLikeStatus = async () => {
    try {
      const response = await axios.get(`${API}/comics/${id}/like-status`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setLiked(response.data.liked);
    } catch (error) {
      console.error('Error checking like status:', error);
    }
  };

  const handleLike = async () => {
    if (!user) {
      toast.error('Please login to like comics');
      return;
    }

    try {
      const response = await axios.post(`${API}/comics/${id}/like`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      setLiked(response.data.liked);
      setComic(prev => ({
        ...prev,
        likes_count: response.data.liked 
          ? prev.likes_count + 1 
          : prev.likes_count - 1
      }));
      
      toast.success(response.data.liked ? 'Comic liked!' : 'Comic unliked!');
    } catch (error) {
      console.error('Error toggling like:', error);
      toast.error('Failed to update like');
    }
  };

  const handleComment = async (e) => {
    e.preventDefault();
    
    if (!user) {
      toast.error('Please login to comment');
      return;
    }

    if (!newComment.trim()) {
      return;
    }

    setSubmittingComment(true);

    try {
      const response = await axios.post(`${API}/comics/${id}/comments`, {
        content: newComment.trim()
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      setComments(prev => [response.data, ...prev]);
      setComic(prev => ({
        ...prev,
        comments_count: prev.comments_count + 1
      }));
      setNewComment('');
      toast.success('Comment added!');
    } catch (error) {
      console.error('Error adding comment:', error);
      toast.error('Failed to add comment');
    } finally {
      setSubmittingComment(false);
    }
  };

  const nextImage = () => {
    if (comic && currentImageIndex < comic.images.length - 1) {
      setCurrentImageIndex(currentImageIndex + 1);
    }
  };

  const prevImage = () => {
    if (currentImageIndex > 0) {
      setCurrentImageIndex(currentImageIndex - 1);
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  // Keyboard navigation
  useEffect(() => {
    const handleKeyPress = (e) => {
      if (!readerMode) return;
      
      if (e.key === 'ArrowDown' || e.key === ' ') {
        e.preventDefault();
        nextImage();
      } else if (e.key === 'ArrowUp') {
        e.preventDefault();
        prevImage();
      } else if (e.key === 'ArrowLeft') {
        e.preventDefault();
        prevEpisode();
      } else if (e.key === 'ArrowRight') {
        e.preventDefault();
        nextEpisode();
      } else if (e.key === 'Escape') {
        e.preventDefault();
        setReaderMode(false);
      }
    };

    if (readerMode) {
      window.addEventListener('keydown', handleKeyPress);
      return () => window.removeEventListener('keydown', handleKeyPress);
    }
  }, [readerMode, currentImageIndex, currentEpisodeIndex]);

  const nextEpisode = () => {
    if (currentEpisodeIndex < seriesComics.length - 1) {
      const nextEpisode = seriesComics[currentEpisodeIndex + 1];
      window.location.href = `/comic/${nextEpisode.id}`;
    }
  };

  const prevEpisode = () => {
    if (currentEpisodeIndex > 0) {
      const prevEpisode = seriesComics[currentEpisodeIndex - 1];
      window.location.href = `/comic/${prevEpisode.id}`;
    }
  };

  const goToEpisode = (episodeId) => {
    window.location.href = `/comic/${episodeId}`;
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-purple-50 via-orange-50 to-yellow-50">
        <div className="container mx-auto px-4 py-8">
          <div className="animate-pulse">
            <div className="h-8 bg-gray-200 rounded mb-4 w-1/3"></div>
            <div className="h-96 bg-gray-200 rounded-2xl mb-6"></div>
            <div className="h-32 bg-gray-200 rounded-xl"></div>
          </div>
        </div>
      </div>
    );
  }

  if (!comic) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-purple-50 via-orange-50 to-yellow-50 flex items-center justify-center">
        <Alert className="max-w-md">
          <AlertDescription>Comic not found.</AlertDescription>
        </Alert>
      </div>
    );
  }

  // Immersive Reader Mode
  if (readerMode && comic && comic.images && comic.images.length > 0) {
    return (
      <div className="fixed inset-0 bg-black z-50 overflow-hidden">
        {/* Reader Controls */}
        <div className="absolute top-4 left-4 right-4 z-10 flex justify-between items-center">
          <div className="flex items-center space-x-4">
            <Button
              onClick={() => setReaderMode(false)}
              variant="ghost"
              size="sm"
              className="text-white hover:bg-white/20"
            >
              <X className="w-5 h-5" />
            </Button>
            
            {comic && comic.series_name && (
              <div className="text-white">
                <span className="text-sm opacity-75">{comic.series_name}</span>
                <span className="ml-2 text-lg font-semibold">Episode {comic.episode_number}</span>
              </div>
            )}
          </div>

          <div className="flex items-center space-x-2">
            <div className="text-white text-sm bg-black/50 px-3 py-1 rounded-full">
              {currentImageIndex + 1} / {comic.images.length}
            </div>
            
            {seriesComics.length > 1 && (
              <Button
                onClick={() => setShowEpisodeList(!showEpisodeList)}
                variant="ghost"
                size="sm" 
                className="text-white hover:bg-white/20"
              >
                <List className="w-5 h-5" />
              </Button>
            )}
          </div>
        </div>

        {/* Episode Navigation */}
        {comic.series_name && (
          <div className="absolute bottom-4 left-1/2 transform -translate-x-1/2 z-10">
            <div className="flex items-center space-x-4">
              {currentEpisodeIndex > 0 && (
                <Button
                  onClick={prevEpisode}
                  variant="ghost"
                  size="sm"
                  className="text-white hover:bg-white/20"
                >
                  <ChevronLeft className="w-5 h-5 mr-1" />
                  Prev Episode
                </Button>
              )}
              
              {currentEpisodeIndex < seriesComics.length - 1 && (
                <Button
                  onClick={nextEpisode}
                  variant="ghost"
                  size="sm"
                  className="text-white hover:bg-white/20"
                >
                  Next Episode
                  <ChevronRight className="w-5 h-5 ml-1" />
                </Button>
              )}
            </div>
          </div>
        )}

        {/* Episode List Sidebar */}
        {showEpisodeList && seriesComics.length > 1 && (
          <div className="absolute top-16 right-4 w-80 max-h-[60vh] bg-black/90 backdrop-blur-sm rounded-lg border border-white/10 overflow-y-auto z-20">
            <div className="p-4">
              <h3 className="text-white font-semibold mb-4">{comic.series_name} Episodes</h3>
              <div className="space-y-2">
                {seriesComics.map((episode, index) => (
                  <button
                    key={episode.id}
                    onClick={() => goToEpisode(episode.id)}
                    className={`w-full text-left p-3 rounded-lg transition-colors ${
                      episode.id === id 
                        ? 'bg-purple-600/50 text-white' 
                        : 'text-gray-300 hover:bg-white/10'
                    }`}
                  >
                    <div className="font-medium">Episode {episode.episode_number}</div>
                    <div className="text-sm opacity-75 truncate">{episode.title}</div>
                  </button>
                ))}
              </div>
            </div>
          </div>
        )}

        {/* Full Screen Vertical Scroll Images */}
        <div 
          className="w-full h-full overflow-y-auto overflow-x-hidden"
          style={{ scrollSnapType: 'y mandatory' }}
        >
          {comic.images.map((image, index) => (
            <div
              key={index}
              className="w-full min-h-screen flex items-center justify-center relative"
              style={{ scrollSnapAlign: 'start' }}
            >
              <img
                src={image}
                alt={`${comic.title} - Page ${index + 1}`}
                className="max-w-full max-h-screen w-auto h-auto object-contain"
                style={{ minHeight: '100vh' }}
              />
              
              {/* Page Navigation Hints */}
              {index > 0 && (
                <div className="absolute top-1/2 left-4 transform -translate-y-1/2 text-white/50">
                  <ArrowUp className="w-6 h-6" />
                </div>
              )}
              
              {index < comic.images.length - 1 && (
                <div className="absolute top-1/2 right-4 transform -translate-y-1/2 text-white/50">
                  <ArrowDown className="w-6 h-6" />
                </div>
              )}
            </div>
          ))}
        </div>

        {/* Keyboard Hints */}
        <div className="absolute bottom-4 left-4 text-white/50 text-xs">
          <div>↑↓ Navigate pages • ←→ Episodes • ESC Exit</div>
        </div>
      </div>
    );
  }

  // Reader Mode Loading State
  if (readerMode && (!comic || !comic.images)) {
    return (
      <div className="fixed inset-0 bg-black z-50 flex items-center justify-center">
        <div className="text-center text-white">
          <div className="mb-4">Loading comic reader...</div>
          <Button
            onClick={() => setReaderMode(false)}
            variant="ghost"
            className="text-white hover:bg-white/20"
          >
            <X className="w-4 h-4 mr-2" />
            Exit Reader
          </Button>
        </div>
      </div>
    );
  }

  // Regular View Mode
  return (
    <div className="min-h-screen bg-gradient-to-br from-purple-50 via-orange-50 to-yellow-50">
      <div className="container mx-auto px-4 py-8">
        {/* Back Button */}
        <Button variant="ghost" asChild className="mb-6 animate-fade-in-up">
          <Link to="/">
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back to Comics
          </Link>
        </Button>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Main Comic Content */}
          <div className="lg:col-span-2 space-y-6">
            {/* Comic Header */}
            <Card className="glass-card animate-fade-in-up">
              <CardContent className="p-6">
                <div className="flex items-start justify-between mb-4">
                  <div className="flex-1">
                    <h1 className="text-3xl font-bold text-gray-800 mb-2">{comic.title}</h1>
                    
                    <div className="flex items-center space-x-4 text-sm text-gray-600 mb-4">
                      <span className="flex items-center">
                        <User className="w-4 h-4 mr-1" />
                        {comic.author_name}
                      </span>
                      <span className="flex items-center">
                        <Calendar className="w-4 h-4 mr-1" />
                        {formatDate(comic.created_at)}
                      </span>
                      {comic.series_name && (
                        <Badge className="series-badge">
                          <BookOpen className="w-3 h-3 mr-1" />
                          {comic.series_name} #{comic.episode_number}
                        </Badge>
                      )}
                    </div>

                    {comic.description && (
                      <p className="text-gray-700 mb-4">{comic.description}</p>
                    )}

                    {/* Categories and Tags */}
                    {(comic.categories.length > 0 || comic.tags.length > 0) && (
                      <div className="flex flex-wrap gap-2 mb-4">
                        {comic.categories.map((category) => (
                          <Badge key={category} variant="secondary" className="category-tag">
                            {category}
                          </Badge>
                        ))}
                        {comic.tags.map((tag) => (
                          <Badge key={tag} variant="outline" className="text-xs">
                            <Tag className="w-3 h-3 mr-1" />
                            {tag}
                          </Badge>
                        ))}
                      </div>
                    )}
                  </div>

                  <Button variant="outline" size="sm" className="bg-white/50">
                    <Share2 className="w-4 h-4" />
                  </Button>
                </div>

                {/* Action Buttons */}
                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-4">
                    <Button
                      onClick={handleLike}
                      variant={liked ? "default" : "outline"}
                      className={liked ? "like-btn liked" : "like-btn"}
                    >
                      <Heart className={`w-4 h-4 mr-2 ${liked ? 'fill-current' : ''}`} />
                      {comic.likes_count} {comic.likes_count === 1 ? 'Like' : 'Likes'}
                    </Button>
                    
                    <Button variant="outline" className="comment-btn">
                      <MessageCircle className="w-4 h-4 mr-2" />
                      {comic.comments_count} {comic.comments_count === 1 ? 'Comment' : 'Comments'}
                    </Button>
                  </div>

                  <div className="flex space-x-2">
                    <Button
                      onClick={() => {
                        console.log('Read Full Screen clicked', { comic: comic, images: comic?.images?.length });
                        if (comic && comic.images && comic.images.length > 0) {
                          setReaderMode(true);
                        } else {
                          toast.error('Comic images are still loading. Please wait a moment.');
                        }
                      }}
                      className="btn-primary"
                      disabled={!comic || !comic.images || comic.images.length === 0}
                    >
                      <Maximize2 className="w-4 h-4 mr-2" />
                      Read Full Screen
                    </Button>
                    
                    {user && comic && user.id === comic.author_id && (
                      <Button asChild variant="outline" className="bg-white/50">
                        <Link to={`/edit-comic/${comic.id}`}>
                          <Edit className="w-4 h-4 mr-2" />
                          Edit Episode
                        </Link>
                      </Button>
                    )}
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Series Episodes - Always show if part of series */}
            {comic.series_name && (
              <Card className="glass-card animate-fade-in-up border-2 border-purple-200">
                <CardHeader>
                  <div className="flex items-center justify-between">
                    <CardTitle className="flex items-center">
                      <BookOpen className="w-5 h-5 mr-2 text-purple-600" />
                      {comic.series_name} - All Episodes
                    </CardTitle>
                    {user && comic && user.id === comic.author_id && (
                      <Button asChild size="sm" variant="outline">
                        <Link to={`/create-comic?series=${comic.series_id}`}>
                          <Plus className="w-4 h-4 mr-2" />
                          Add Episode
                        </Link>
                      </Button>
                    )}
                  </div>
                </CardHeader>
                <CardContent>
                  {seriesComics.length === 0 ? (
                    <div className="text-center py-8 text-gray-500">
                      <BookOpen className="w-12 h-12 mx-auto mb-4 opacity-50" />
                      <p>Loading series episodes...</p>
                    </div>
                  ) : (
                    <div className="space-y-3">
                      {seriesComics.map((episode, index) => (
                        <div
                          key={episode.id}
                          className={`flex items-center space-x-4 p-4 rounded-lg border transition-all ${
                            episode.id === id
                              ? 'bg-purple-100 border-purple-300 ring-2 ring-purple-200'
                              : 'bg-white/50 border-gray-200 hover:border-purple-200 hover:bg-purple-50'
                          }`}
                        >
                          {episode.images.length > 0 && (
                            <img
                              src={episode.images[0]}
                              alt={episode.title}
                              className="w-20 h-20 object-cover rounded-lg flex-shrink-0"
                            />
                          )}
                          
                          <div className="flex-1">
                            <div className="flex items-center space-x-2 mb-1">
                              <Badge variant="outline" className="bg-purple-100 text-purple-700">
                                Episode {episode.episode_number}
                              </Badge>
                              {episode.id === id && (
                                <Badge className="bg-green-100 text-green-700">Currently Reading</Badge>
                              )}
                            </div>
                            <h3 className="font-semibold text-gray-800 mb-1">{episode.title}</h3>
                            <p className="text-gray-600 text-sm line-clamp-1 mb-2">{episode.description}</p>
                            <div className="flex items-center space-x-4 text-xs text-gray-500">
                              <span>{episode.images.length} slides</span>
                              <span>{formatDate(episode.created_at)}</span>
                              <div className="flex items-center space-x-2">
                                <Heart className="w-3 h-3" />
                                <span>{episode.likes_count}</span>
                                <MessageCircle className="w-3 h-3" />
                                <span>{episode.comments_count}</span>
                              </div>
                            </div>
                          </div>
                          
                          <div className="flex flex-col space-y-2">
                            {episode.id !== id && (
                              <Button asChild size="sm" variant="outline">
                                <Link to={`/comic/${episode.id}`}>
                                  Read
                                </Link>
                              </Button>
                            )}
                            {user && comic && user.id === comic.author_id && (
                              <Button asChild size="sm" variant="ghost">
                                <Link to={`/edit-comic/${episode.id}`}>
                                  <Edit className="w-3 h-3 mr-1" />
                                  Edit
                                </Link>
                              </Button>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </CardContent>
              </Card>
            )}

            {/* Comic Preview */}
            <Card className="glass-card animate-slide-in-right">
              <CardContent className="p-0">
                {comic.images.length > 0 && (
                  <div className="relative">
                    <img
                      src={comic.images[currentImageIndex]}
                      alt={`${comic.title} - Page ${currentImageIndex + 1}`}
                      className="w-full max-h-[400px] object-contain rounded-t-2xl cursor-pointer"
                      onClick={() => setReaderMode(true)}
                    />
                    
                    <div className="absolute inset-0 bg-black/0 hover:bg-black/10 flex items-center justify-center transition-all cursor-pointer rounded-t-2xl"
                         onClick={() => setReaderMode(true)}>
                      <div className="opacity-0 hover:opacity-100 transition-opacity">
                        <Maximize2 className="w-12 h-12 text-white drop-shadow-lg" />
                      </div>
                    </div>
                    
                    {comic.images.length > 1 && (
                      <>
                        <div className="absolute bottom-4 left-1/2 transform -translate-x-1/2 bg-black bg-opacity-50 text-white px-3 py-1 rounded-full text-sm">
                          {currentImageIndex + 1} / {comic.images.length}
                        </div>
                        
                        {currentImageIndex > 0 && (
                          <Button
                            onClick={prevImage}
                            variant="outline"
                            size="sm"
                            className="absolute left-4 top-1/2 transform -translate-y-1/2 bg-white/80 hover:bg-white"
                          >
                            <ChevronLeft className="w-4 h-4" />
                          </Button>
                        )}
                        
                        {currentImageIndex < comic.images.length - 1 && (
                          <Button
                            onClick={nextImage}
                            variant="outline"
                            size="sm"
                            className="absolute right-4 top-1/2 transform -translate-y-1/2 bg-white/80 hover:bg-white"
                          >
                            <ChevronRight className="w-4 h-4" />
                          </Button>
                        )}
                      </>
                    )}
                  </div>
                )}
                
                {comic.images.length > 1 && (
                  <div className="p-4 border-t border-gray-200">
                    <div className="grid grid-cols-6 gap-2">
                      {comic.images.map((image, index) => (
                        <button
                          key={index}
                          onClick={() => setCurrentImageIndex(index)}
                          className={`aspect-square rounded-lg overflow-hidden border-2 ${
                            index === currentImageIndex ? 'border-purple-500' : 'border-transparent'
                          }`}
                        >
                          <img
                            src={image}
                            alt={`Page ${index + 1}`}
                            className="w-full h-full object-cover"
                          />
                        </button>
                      ))}
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>
          </div>

          {/* Comments Section */}
          <div className="space-y-6">
            {/* Add Comment */}
            {user && (
              <Card className="glass-card animate-slide-in-right">
                <CardHeader>
                  <CardTitle className="text-lg">Add Comment</CardTitle>
                </CardHeader>
                <CardContent>
                  <form onSubmit={handleComment} className="space-y-4">
                    <Textarea
                      value={newComment}
                      onChange={(e) => setNewComment(e.target.value)}
                      placeholder="Share your thoughts about this comic..."
                      className="form-input resize-none"
                      rows={3}
                    />
                    <Button
                      type="submit"
                      disabled={submittingComment || !newComment.trim()}
                      className="w-full btn-primary"
                    >
                      {submittingComment ? (
                        "Posting..."
                      ) : (
                        <>
                          <Send className="w-4 h-4 mr-2" />
                          Post Comment
                        </>
                      )}
                    </Button>
                  </form>
                </CardContent>
              </Card>
            )}

            {/* Comments List */}
            <Card className="glass-card animate-slide-in-right">
              <CardHeader>
                <CardTitle className="text-lg">Comments ({comments.length})</CardTitle>
              </CardHeader>
              <CardContent>
                {comments.length === 0 ? (
                  <div className="text-center py-8 text-gray-500">
                    <MessageCircle className="w-12 h-12 mx-auto mb-2 opacity-50" />
                    <p>No comments yet. Be the first to comment!</p>
                  </div>
                ) : (
                  <div className="space-y-4">
                    {comments.map((comment) => (
                      <div key={comment.id} className="border-b border-gray-200 pb-4 last:border-b-0">
                        <div className="flex items-start space-x-3">
                          <Avatar className="w-8 h-8">
                            <AvatarFallback className="bg-gradient-to-r from-purple-400 to-pink-400 text-white text-sm">
                              {comment.author_name.charAt(0).toUpperCase()}
                            </AvatarFallback>
                          </Avatar>
                          
                          <div className="flex-1">
                            <div className="flex items-center space-x-2 mb-1">
                              <span className="font-semibold text-sm text-gray-800">
                                {comment.author_name}
                              </span>
                              <span className="text-xs text-gray-500">
                                {formatDate(comment.created_at)}
                              </span>
                            </div>
                            <p className="text-gray-700 text-sm">{comment.content}</p>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ComicView;